import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { Sparkles, Mail, CheckCircle2, MessageCircle, Brain, Heart } from "lucide-react";
import type { User } from "@shared/models/auth";

interface Props {
  user: User;
}

/**
 * First-run onboarding shown once per account: a short welcome, then a
 * nudge to confirm email if it isn't confirmed yet. Gated by
 * `onboardingCompletedAt` on the user row (server-side, so it survives
 * across devices) rather than localStorage — see hasEnteredInterface()
 * in lib/entered.ts for why that one intentionally stays client-side.
 */
export function OnboardingFlow({ user }: Props) {
  const [open, setOpen] = useState(!user.onboardingCompletedAt);
  const [step, setStep] = useState<"welcome" | "verify">("welcome");
  const [resending, setResending] = useState(false);
  const [resent, setResent] = useState(false);
  const { toast } = useToast();
  const queryClient = useQueryClient();

  async function finish() {
    setOpen(false);
    try {
      await fetch("/api/auth/complete-onboarding", { method: "POST", credentials: "include" });
      queryClient.invalidateQueries({ queryKey: ["/api/auth/user"] });
    } catch {
      // Non-fatal — worst case the flow shows again next visit.
    }
  }

  async function resendVerification() {
    setResending(true);
    try {
      const res = await fetch("/api/auth/resend-verification", { method: "POST", credentials: "include" });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Couldn't resend the email");
      setResent(true);
      toast({ title: "Verification email sent", description: `Check ${user.email} for the link.` });
    } catch (err) {
      toast({
        title: "Couldn't resend",
        description: err instanceof Error ? err.message : "Please try again.",
        variant: "destructive",
      });
    } finally {
      setResending(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={(next) => { if (!next) finish(); }}>
      <DialogContent className="sm:max-w-md" data-testid="dialog-onboarding">
        {step === "welcome" ? (
          <>
            <DialogHeader>
              <div className="mx-auto mb-2 h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center">
                <Sparkles className="h-6 w-6 text-primary" />
              </div>
              <DialogTitle className="text-center text-xl">
                Welcome{user.firstName ? `, ${user.firstName}` : ""}
              </DialogTitle>
              <DialogDescription className="text-center">
                A quick look before you start.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3 py-2">
              <div className="flex items-start gap-3">
                <MessageCircle className="h-4 w-4 mt-0.5 text-amber-500 flex-shrink-0" />
                <p className="text-sm text-muted-foreground">Talk to Memnora like you would a person — it remembers your conversation as you go.</p>
              </div>
              <div className="flex items-start gap-3">
                <Brain className="h-4 w-4 mt-0.5 text-amber-500 flex-shrink-0" />
                <p className="text-sm text-muted-foreground">Paid tiers extend how much it remembers long-term — free works great to start.</p>
              </div>
              <div className="flex items-start gap-3">
                <Heart className="h-4 w-4 mt-0.5 text-amber-500 flex-shrink-0" />
                <p className="text-sm text-muted-foreground">You're in control — export or delete your data any time from account settings.</p>
              </div>
            </div>
            <Button
              className="w-full"
              onClick={() => (user.emailVerified ? finish() : setStep("verify"))}
              data-testid="button-onboarding-next"
            >
              {user.emailVerified ? "Start chatting" : "Continue"}
            </Button>
          </>
        ) : (
          <>
            <DialogHeader>
              <div className="mx-auto mb-2 h-12 w-12 rounded-full bg-amber-500/10 flex items-center justify-center">
                <Mail className="h-6 w-6 text-amber-500" />
              </div>
              <DialogTitle className="text-center text-xl">Confirm your email</DialogTitle>
              <DialogDescription className="text-center">
                We sent a confirmation link to <span className="font-medium text-foreground">{user.email}</span>.
              </DialogDescription>
            </DialogHeader>
            <div className="py-2 space-y-3">
              {resent && (
                <div className="flex items-center gap-2 text-sm text-emerald-500" data-testid="text-verification-resent">
                  <CheckCircle2 className="h-4 w-4" />
                  Sent — check your inbox (and spam folder).
                </div>
              )}
              <p className="text-sm text-muted-foreground">
                Confirming your email protects your account and lets us reach you about your subscription and receipts. You can still use Memnora before confirming.
              </p>
              <Button
                variant="outline"
                className="w-full"
                onClick={resendVerification}
                disabled={resending}
                data-testid="button-resend-verification"
              >
                {resending ? "Sending…" : "Resend confirmation email"}
              </Button>
            </div>
            <Button className="w-full" onClick={finish} data-testid="button-onboarding-finish">
              Start chatting
            </Button>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
